import { useState } from 'react'
import { motion } from 'framer-motion'
import { Helmet } from 'react-helmet-async'
import { useNavigate } from 'react-router-dom'
import {
    AcademicCapIcon,
    UserGroupIcon,
    TrophyIcon,
    HeartIcon,
    CheckCircleIcon,
    ArrowRightIcon
} from '@heroicons/react/24/outline'
import EnquiryModal from '../../components/Enquiry/EnquiryModal'

const About = () => {
    const navigate = useNavigate()
    const [isEnquiryOpen, setIsEnquiryOpen] = useState(false)
    const [enquiryType, setEnquiryType] = useState('consultation')

    const stats = [
        { number: '10,000+', label: 'Students Helped', icon: UserGroupIcon },
        { number: '50+', label: 'Partner Universities', icon: AcademicCapIcon },
        { number: '98%', label: 'Success Rate', icon: TrophyIcon },
        { number: '24/7', label: 'Support Available', icon: HeartIcon },
    ]

    const values = [
        {
            title: 'Excellence',
            description: 'We strive for excellence in everything we do, ensuring the highest quality education guidance.',
            icon: TrophyIcon,
        },
        {
            title: 'Integrity',
            description: 'We maintain the highest standards of integrity and transparency in all our interactions.',
            icon: CheckCircleIcon,
        },
        {
            title: 'Innovation',
            description: 'We embrace innovation to provide cutting-edge solutions for education guidance.',
            icon: AcademicCapIcon,
        },
        {
            title: 'Care',
            description: 'We care deeply about our students\' success and are committed to their growth.',
            icon: HeartIcon,
        },
    ]



    return (
        <>
            <Helmet>
                <title>About Us | MyMargdarshak</title>
                <meta name="description" content="Learn about MyMargdarshak's mission to provide quality education guidance. Meet our expert team and discover our commitment to student success." />
                <meta name="keywords" content="about mymargdarshak, education guidance, career counseling, online education, student success" />
            </Helmet>

            <div className="min-h-screen bg-gray-50 pt-0">
                {/* Premium Light Theme About Hero Section */}
                <section className="relative pt-28 lg:pt-40 pb-20 lg:pb-32 overflow-hidden bg-white border-b border-gray-100">
                    {/* Dynamic Mesh Gradient Background - Subtle for Light Theme */}
                    <div className="absolute inset-0 z-0">
                        <div className="absolute top-[-10%] left-[-10%] w-[50%] h-[50%] rounded-full bg-primary-100 blur-[120px] animate-pulse"></div>
                        <div className="absolute bottom-[-10%] right-[-10%] w-[50%] h-[50%] rounded-full bg-indigo-100 blur-[120px] animate-pulse" style={{ animationDelay: '2s' }}></div>
                    </div>

                    {/* Subtle Overlay Grid */}
                    <div className="absolute inset-0 z-0 opacity-[0.05]" style={{ backgroundImage: 'radial-gradient(#000 1px, transparent 1px)', backgroundSize: '40px 40px' }}></div>

                    <div className="relative z-10 container mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                            <motion.div
                                initial={{ opacity: 0, x: -30 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.7 }}
                                className="text-left"
                            >
                                <motion.div
                                    initial={{ opacity: 0, scale: 0.8 }}
                                    animate={{ opacity: 1, scale: 1 }}
                                    className="inline-flex items-center space-x-2 bg-primary-50 border border-primary-100 px-4 py-2 rounded-full mb-8"
                                >
                                    <span className="w-2 h-2 bg-primary-600 rounded-full animate-ping"></span>
                                    <span className="text-sm font-black text-primary-700 uppercase tracking-widest">Our Story</span>
                                </motion.div>

                                <h1 className="text-4xl sm:text-6xl lg:text-7xl font-black mb-8 text-slate-900 leading-tight">
                                    Guiding Your<br />
                                    <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary-600 to-indigo-600">Future Potential</span>
                                </h1>
                                
                                <p className="text-lg sm:text-xl text-slate-600 mb-10 max-w-xl font-medium leading-relaxed">
                                    MyMargdarshak is more than a counseling platform. We are your dedicated partner in navigating the complex world of higher education.
                                </p>

                                <div className="flex flex-wrap gap-4">
                                    <button 
                                        onClick={() => setIsEnquiryOpen(true)}
                                        className="px-8 py-4 bg-primary-600 hover:bg-primary-700 text-white rounded-2xl font-bold text-lg transition-all transform hover:scale-105 shadow-xl shadow-primary-900/20"
                                    >
                                        Start Your Journey
                                    </button>
                                </div>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, x: 30 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.7, delay: 0.2 }}
                                className="relative lg:block"
                            >
                                <div className="grid grid-cols-2 gap-6">
                                    {stats.map((stat, index) => {
                                        const Icon = stat.icon
                                        return (
                                            <motion.div 
                                                key={index} 
                                                whileHover={{ y: -5 }}
                                                className="bg-white/80 backdrop-blur-xl border border-slate-200 p-8 rounded-3xl shadow-lg shadow-slate-100"
                                            >
                                                <div className="w-12 h-12 bg-primary-50 rounded-2xl flex items-center justify-center mb-4 border border-primary-100">
                                                    <Icon className="w-6 h-6 text-primary-600" />
                                                </div>
                                                <div className="text-3xl font-black text-slate-900 mb-1">
                                                    {stat.number}
                                                </div>
                                                <div className="text-sm text-slate-500 font-bold uppercase tracking-wider">
                                                    {stat.label}
                                                </div>
                                            </motion.div>
                                        )
                                    })}
                                </div>

                                {/* Floating Decorative Element */}
                                <div className="absolute -top-10 -right-10 w-32 h-32 bg-accent-100 rounded-full blur-3xl animate-pulse"></div>
                                <div className="absolute -bottom-10 -left-10 w-32 h-32 bg-primary-100 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }}></div>
                            </motion.div>
                        </div>
                    </div>
                </section>

                {/* Mission Section */}
                <section className="py-16 bg-white">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                            <motion.div
                                initial={{ opacity: 0, x: -20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6 }}
                            >
                                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
                                    Our Mission
                                </h2>
                                <p className="text-lg text-gray-600 mb-6 leading-relaxed">
                                    At MyMargdarshak, we believe that quality education should be accessible to everyone.
                                    Our mission is to guide students toward the right educational path that aligns with
                                    their career aspirations and personal goals.
                                </p>
                                <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                                    We partner with top universities and institutions to provide comprehensive
                                    education guidance, from course selection to career planning, ensuring our
                                    students make informed decisions for their future.
                                </p>
                                <div className="flex flex-col sm:flex-row gap-4">
                                    <button
                                        className="btn-primary btn-lg"
                                        onClick={() => {
                                            setEnquiryType('consultation')
                                            setIsEnquiryOpen(true)
                                        }}
                                    >
                                        Get Started
                                    </button>
                                    <button
                                        className="btn-outline btn-lg"
                                        onClick={() => navigate('/courses')}
                                    >
                                        Learn More
                                    </button>
                                </div>
                            </motion.div>

                            <motion.div
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                transition={{ duration: 0.6, delay: 0.2 }}
                                className="relative"
                            >
                                <div className="bg-gradient-to-br from-primary-100 to-primary-200 rounded-2xl p-8">
                                    <div className="grid grid-cols-2 gap-6">
                                        {stats.map((stat, index) => {
                                            const Icon = stat.icon
                                            return (
                                                <div key={index} className="text-center">
                                                    <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4">
                                                        <Icon className="w-8 h-8 text-primary-600" />
                                                    </div>
                                                    <div className="text-2xl font-bold text-gray-900 mb-1">
                                                        {stat.number}
                                                    </div>
                                                    <div className="text-sm text-gray-600">
                                                        {stat.label}
                                                    </div>
                                                </div>
                                            )
                                        })}
                                    </div>
                                </div>
                            </motion.div>
                        </div>
                    </div>
                </section>

                {/* Values Section */}
                <section className="py-16 bg-gray-50">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                            className="text-center mb-12"
                        >
                            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
                                Our Values
                            </h2>
                            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
                                The principles that guide everything we do
                            </p>
                        </motion.div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                            {values.map((value, index) => {
                                const Icon = value.icon
                                return (
                                    <motion.div
                                        key={index}
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.6, delay: index * 0.1 }}
                                        className="card p-6 text-center hover-lift"
                                    >
                                        <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-4">
                                            <Icon className="w-8 h-8 text-primary-600" />
                                        </div>
                                        <h3 className="text-xl font-semibold text-gray-900 mb-3">
                                            {value.title}
                                        </h3>
                                        <p className="text-gray-600">
                                            {value.description}
                                        </p>
                                    </motion.div>
                                )
                            })}
                        </div>
                    </div>
                </section>

                {/* CTA Section */}
                <section className="py-16 bg-gradient-to-br from-primary-600 via-primary-700 to-primary-800 text-white">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <motion.div
                            initial={{ opacity: 0, y: 20 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ duration: 0.6 }}
                        >
                            <h2 className="text-3xl sm:text-4xl font-bold mb-6 text-white">
                                Ready to Start Your Journey?
                            </h2>
                            <p className="text-xl mb-8 max-w-3xl mx-auto text-white drop-shadow-md">
                                Join thousands of students who have transformed their careers with our guidance
                            </p>
                            <div className="flex flex-col sm:flex-row gap-4 justify-center">
                                <button
                                    className="btn-lg bg-white text-primary-600 hover:bg-gray-50"
                                    onClick={() => {
                                        setEnquiryType('consultation')
                                        setIsEnquiryOpen(true)
                                    }}
                                >
                                    Get Free Consultation
                                </button>
                                <button
                                    className="btn-lg border-2 border-white text-white hover:bg-white hover:text-primary-600"
                                    onClick={() => navigate('/courses')}
                                >
                                    Explore Courses
                                </button>
                            </div>
                        </motion.div>
                    </div>
                </section>

                {/* Our Journey Timeline */}
                <section className="py-24 bg-white relative overflow-hidden">
                    <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                        <div className="text-center mb-20">
                            <h2 className="text-4xl font-black text-gray-900 mb-4 tracking-tight">Our <span className="text-primary-600">Journey</span></h2>
                            <p className="text-lg text-gray-500 max-w-2xl mx-auto font-medium">How we became India's most trusted education guidance partner.</p>
                        </div>

                        <div className="relative max-w-4xl mx-auto">
                            {/* Vertical Line */}
                            <div className="absolute left-1/2 -translate-x-1/2 h-full w-0.5 bg-gradient-to-b from-primary-100 via-primary-200 to-transparent"></div>

                            {[
                                {
                                    year: "2018",
                                    title: "The Genesis",
                                    desc: "Founded with a vision to bridge the gap between quality education and aspiring students across India."
                                },
                                {
                                    year: "2020",
                                    title: "Digital Transformation",
                                    desc: "Pivot to a purely digital platform, partnering with top 10 online and distance learning universities."
                                },
                                {
                                    year: "2022",
                                    title: "Milestone: 5,000+ Students",
                                    desc: "Reached a major milestone of helping over 5,000 students find their ideal university degree."
                                },
                                {
                                    year: "2024",
                                    title: "The Premium Era",
                                    desc: "Launching our new AI-driven counseling platform and expanded network of 50+ partner institutions."
                                }
                            ].map((milestone, idx) => (
                                <motion.div
                                    key={idx}
                                    initial={{ opacity: 0, x: idx % 2 === 0 ? -50 : 50 }}
                                    whileInView={{ opacity: 1, x: 0 }}
                                    viewport={{ once: true }}
                                    className={`flex items-center mb-16 relative z-10 ${idx % 2 === 0 ? 'flex-row-reverse' : ''}`}
                                >
                                    <div className="w-1/2"></div>
                                    <div className="w-10 h-10 rounded-full bg-white border-4 border-primary-500 shadow-xl flex items-center justify-center relative mx-4">
                                        <div className="w-2 h-2 rounded-full bg-primary-500 animate-ping"></div>
                                    </div>
                                    <div className={`w-1/2 p-8 rounded-[2.5rem] bg-gray-50 border border-gray-100 hover:shadow-2xl transition-all duration-500 ${idx % 2 === 0 ? 'text-right' : 'text-left'}`}>
                                        <span className="text-accent-600 font-black text-xl mb-2 block">{milestone.year}</span>
                                        <h3 className="text-2xl font-black text-gray-900 mb-3">{milestone.title}</h3>
                                        <p className="text-gray-500 font-medium leading-relaxed">{milestone.desc}</p>
                                    </div>
                                </motion.div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Enquiry Modal */}
                <EnquiryModal
                    isOpen={isEnquiryOpen}
                    onClose={() => setIsEnquiryOpen(false)}
                    enquiryType={enquiryType}
                />
            </div>
        </>
    )
}

export default About
